<template>
  <div class="shorten-tool">
    <template v-if="!linkResult">
      <!-- One-line creator: destination → custom name → go. A blank custom
           name means the backend generates a slug. -->
      <div class="section-label">🔗 Shorten a link</div>
      <div class="creator-card">
        <div class="url-wrap">
          <span class="link-icon">🔗</span>
          <input
            v-model="linkInput"
            class="url-input mono"
            placeholder="Paste a long URL…"
            @keydown.enter="createShortLink('url')"
          />
        </div>
        <div class="slug-seg" :class="{ invalid: slugInvalid }" title="Optional custom name — leave blank and we'll generate one">
          <span class="slug-host mono">{{ shortHost }}/</span>
          <input
            v-model="slugInput"
            class="slug-input mono"
            placeholder="custom-name"
            maxlength="64"
            @keydown.enter="createShortLink('url')"
          />
        </div>
        <button class="lk-btn lk-btn-primary shorten-btn" :disabled="creating" @click="createShortLink('url')">
          <span v-if="creating" class="btn-spinner"></span>
          <span>{{ creating ? 'Working…' : 'Shorten →' }}</span>
        </button>
      </div>
      <div class="creator-hint">
        Custom name is optional — leave it blank and we'll generate one.
      </div>

      <!-- File path: drop/browse right here (anonymous friendly), or pick one
           of your existing files when signed in. -->
      <div class="section-label file-section">📎 Share a file</div>
      <template v-if="fileSource === 'existing' && authedNow">
        <div class="file-card-row">
          <div class="file-select-wrap">
            <span class="link-icon">📎</span>
            <select v-model="fileIdInput" class="file-select mono">
              <option value="" disabled>{{ files.length ? 'Choose one of your files…' : 'No files uploaded yet' }}</option>
              <option v-for="f in files" :key="f.file_id" :value="f.file_id">{{ f.name }}</option>
            </select>
          </div>
          <div class="slug-seg" :class="{ invalid: fileSlugInvalid }" title="Optional custom name — leave blank and we'll generate one">
            <span class="slug-host mono">{{ shortHost }}/</span>
            <input v-model="fileSlugInput" class="slug-input mono" placeholder="custom-name" maxlength="64" />
          </div>
          <button class="lk-btn lk-btn-primary shorten-btn" :disabled="creating || !files.length" @click="createShortLink('file')">
            <span v-if="creating" class="btn-spinner"></span>
            <span>{{ creating ? 'Creating…' : 'Create link →' }}</span>
          </button>
        </div>
        <button class="src-toggle" @click="fileSource = 'upload'">↑ …or upload a new file</button>
      </template>
      <template v-else>
        <FileUploadComponent embedded @uploaded="onFileUploaded" @reset="onUploadReset" />

        <!-- Post-upload: the file link exists on its own; a short link is an
             explicit opt-in (works without an account). -->
        <div v-if="fileIdInput" class="add-link-row">
          <span class="add-link-label">Add a short link to this file</span>
          <div class="add-link-controls">
            <div class="slug-seg" :class="{ invalid: fileSlugInvalid }" title="Optional custom name — leave blank and we'll generate one">
              <span class="slug-host mono">{{ shortHost }}/</span>
              <input v-model="fileSlugInput" class="slug-input mono" placeholder="custom-name" maxlength="64" @keydown.enter="createShortLink('file')" />
            </div>
            <button class="lk-btn lk-btn-primary" :disabled="creating" @click="createShortLink('file')">
              {{ creating ? 'Creating…' : '+ Short link' }}
            </button>
          </div>
        </div>

        <button v-if="authedNow && !fileIdInput" class="src-toggle" @click="switchToExisting">📎 …or choose one of your files</button>
      </template>

      <!-- Paste path: pastes live at their own /p/… page; a short link is the
           same explicit opt-in as for files. Existing pastes only — a fresh
           paste offers its short link right in the paste tool. -->
      <template v-if="authedNow">
        <div class="section-label file-section">📋 Link a paste</div>
        <div class="file-card-row">
          <div class="file-select-wrap">
            <span class="link-icon">📋</span>
            <select v-model="pasteIdInput" class="file-select mono">
              <option value="" disabled>{{ pastes.length ? 'Choose one of your pastes…' : 'No pastes yet' }}</option>
              <option v-for="p in pastes" :key="p.paste_id" :value="p.paste_id">{{ p.title || 'Untitled paste' }}</option>
            </select>
          </div>
          <div class="slug-seg" :class="{ invalid: pasteSlugInvalid }" title="Optional custom name — leave blank and we'll generate one">
            <span class="slug-host mono">{{ shortHost }}/</span>
            <input v-model="pasteSlugInput" class="slug-input mono" placeholder="custom-name" maxlength="64" @keydown.enter="createShortLink('paste')" />
          </div>
          <button class="lk-btn lk-btn-primary shorten-btn" :disabled="creating || !pastes.length" @click="createShortLink('paste')">
            <span v-if="creating" class="btn-spinner"></span>
            <span>{{ creating ? 'Creating…' : 'Create link →' }}</span>
          </button>
        </div>
      </template>

      <div v-if="error" class="shorten-error">{{ error }}</div>

      <div class="samples">
        <span class="samples-label">Try:</span>
        <button v-for="(s, i) in samples" :key="i" class="sample-chip mono" @click="linkInput = s.url">{{ s.label }}</button>
      </div>
    </template>

    <!-- RESULT -->
    <div v-if="linkResult" class="result-card">
      <div class="result-stripe"></div>
      <div class="result-body">
        <div class="result-main">
          <div class="result-ok"><span class="ok-badge">✓</span> Your short link is ready</div>
          <div class="result-link">
            <span class="result-short mono">{{ shortDisplayVal }}</span>
            <CopyButton :text="shortUrlVal" small />
          </div>
          <div class="result-dest">
            <span class="faint">→</span>
            <span v-if="linkResult.type === 'file'" class="faint">📎</span>
            <span class="mono ellipsis">{{ resultDestination }}</span>
          </div>
          <div class="result-actions">
            <button class="lk-btn" @click="viewAnalytics">View analytics</button>
            <button class="lk-btn" @click="resetLink">Shorten another</button>
          </div>
        </div>
        <div class="result-qr">
          <QrCode ref="qr" :value="shortUrlVal" :size="132" />
          <button class="lk-btn-ghost dl-qr" @click="downloadQr">↓ Download QR</button>
        </div>
      </div>
      <div v-if="linkResult.claim_token" class="result-claim">
        <span class="claim-text">🔒 Sign in to keep this link, edit the slug &amp; track clicks.</span>
        <button class="lk-btn lk-btn-primary" @click="claim">Sign in &amp; claim</button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import QrCode from '@/components/ui/QrCode.vue';
import CopyButton from '@/components/ui/CopyButton.vue';
import FileUploadComponent from '@/components/FileUploadComponent.vue';
import { shortUrl, shortDisplay, shortBase, normalizeUrl, isValidSlug } from '@/utils/links';

export default {
  name: 'ShortenTool',
  components: { QrCode, CopyButton, FileUploadComponent },
  data() {
    return {
      // File source: upload a new file (works anonymously) or pick an
      // existing one (signed-in only).
      fileSource: 'upload',
      pendingUploadName: '',
      linkInput: '',
      fileIdInput: '',
      pasteIdInput: '',
      slugInput: '',
      fileSlugInput: '',
      pasteSlugInput: '',
      error: '',
      creating: false,
      linkResult: null,
      samples: [
        { label: 'github.com/…', url: 'https://github.com/linker-to/sdk/releases' },
        { label: 'a very long URL', url: 'https://example.com/products/category/subcategory/item?ref=newsletter&utm_source=email' },
      ],
    };
  },
  computed: {
    ...mapGetters('auth', { authedNow: 'isAuthenticated' }),
    ...mapGetters('files', ['stateFiles']),
    ...mapGetters('pastes', ['statePastes']),
    files() { return this.stateFiles || []; },
    pastes() {
      // Expired pastes can't be linked (the backend refuses); hide them.
      const now = Date.now();
      return (this.statePastes || []).filter(
        (p) => !p.expires || new Date(p.expires).getTime() > now
      );
    },
    shortDisplayVal() { return this.linkResult ? shortDisplay(this.linkResult.slug) : ''; },
    shortUrlVal() { return this.linkResult ? shortUrl(this.linkResult.slug) : ''; },
    shortHost() { return shortBase().replace(/^https?:\/\//, ''); },
    slugInvalid() {
      const slug = (this.slugInput || '').trim();
      return !!slug && !isValidSlug(slug);
    },
    fileSlugInvalid() {
      const slug = (this.fileSlugInput || '').trim();
      return !!slug && !isValidSlug(slug);
    },
    pasteSlugInvalid() {
      const slug = (this.pasteSlugInput || '').trim();
      return !!slug && !isValidSlug(slug);
    },
    resultDestination() {
      if (!this.linkResult) return '';
      if (this.linkResult.type === 'file') {
        const f = this.files.find(x => x.file_id === this.linkResult.file_id);
        return f ? f.name : (this.pendingUploadName || this.linkResult.file_id);
      }
      if (this.linkResult.type === 'paste') {
        const p = (this.statePastes || []).find(x => x.paste_id === this.linkResult.paste_id);
        return p ? (p.title || 'Untitled paste') : this.linkResult.paste_id;
      }
      return this.linkResult.redirect_to;
    },
  },
  watch: {
    // The paste picker is always visible when signed in; load lazily.
    authedNow: {
      immediate: true,
      handler(v) {
        if (v && !this.statePastes) this.getPastes().catch(() => {});
      },
    },
  },
  methods: {
    ...mapActions('shortLinks', ['createLink']),
    ...mapActions('files', ['getFiles']),
    ...mapActions('pastes', ['getPastes']),
    switchToExisting() {
      this.fileSource = 'existing';
      this.fileIdInput = '';
      if (!this.stateFiles) this.getFiles().catch(() => {});
    },
    onFileUploaded({ fileId, name }) {
      // The upload stands on its own (the file page link is already shown by
      // the uploader). A short link is a separate, explicit step — the
      // "Add a short link" row this enables.
      this.fileIdInput = fileId;
      this.pendingUploadName = name;
    },
    onUploadReset() {
      // "Share another" in the uploader: drop the pending file so the
      // add-a-short-link row disappears with it.
      this.fileIdInput = '';
      this.pendingUploadName = '';
      this.fileSlugInput = '';
    },
    async createShortLink(kind = 'url') {
      if (this.creating) return;
      this.error = '';

      // Validate an optional custom slug; empty means the backend generates one.
      const slugSource = kind === 'file' ? this.fileSlugInput
        : kind === 'paste' ? this.pasteSlugInput
          : this.slugInput;
      const slug = (slugSource || '').trim();
      if (slug && !isValidSlug(slug)) {
        this.error = 'Custom name may only contain letters, numbers, - and _';
        return;
      }

      const payload = {};
      if (kind === 'file') {
        if (!this.fileIdInput) { this.error = 'Pick a file to link to'; return; }
        payload.type = 'file';
        payload.fileId = this.fileIdInput;
      } else if (kind === 'paste') {
        if (!this.pasteIdInput) { this.error = 'Pick a paste to link to'; return; }
        payload.type = 'paste';
        payload.pasteId = this.pasteIdInput;
      } else {
        // Validate the destination URL properly instead of blindly prefixing.
        const norm = normalizeUrl(this.linkInput);
        if (!norm.ok) { this.error = norm.error; return; }
        payload.type = 'redirect';
        payload.redirectTo = norm.url;
      }
      if (slug) payload.slug = slug;

      this.creating = true;
      try {
        this.linkResult = await this.createLink(payload);
      } catch (e) {
        // store surfaces a toast on failure (e.g. slug already taken)
      } finally {
        this.creating = false;
      }
    },
    resetLink() {
      this.linkResult = null;
      this.linkInput = '';
      this.fileIdInput = '';
      this.pasteIdInput = '';
      this.pendingUploadName = '';
      this.slugInput = '';
      this.fileSlugInput = '';
      this.pasteSlugInput = '';
      this.error = '';
    },
    viewAnalytics() {
      if (this.linkResult) this.$router.push(`/dashboard/link/${this.linkResult.slug}`);
    },
    downloadQr() {
      if (this.$refs.qr) this.$refs.qr.download(`linker-${this.linkResult.slug}-qr.png`);
    },
    claim() {
      // Sign in, land back on this link's detail page, and let it auto-claim
      // with the stashed claim token (?claim=1 is picked up there).
      this.$router.push({
        name: 'Login',
        query: { redirect: `/dashboard/link/${this.linkResult.slug}?claim=1` },
      });
    },
  },
};
</script>

<style scoped>
.shorten-tool { width: 100%; }

/* ---- Section headers ---- */
.section-label {
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 600;
  color: var(--faint);
  text-transform: uppercase;
  letter-spacing: .14em;
  margin-bottom: 8px;
  text-align: left;
}
.section-label.file-section { margin-top: 22px; }

/* ---- One-line creator ---- */
.creator-card {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background: var(--field);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  animation: lk-pop .35s ease both;
  flex-wrap: wrap;
}
.url-wrap {
  flex: 1 1 220px;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding-left: 12px;
}
.link-icon { color: var(--accent); font-size: 16px; }
.url-input {
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  color: var(--text);
  font-size: 15px;
  padding: 12px 0;
}

/* Custom-name pill: a quiet inset segment that reads as "your short URL". */
.slug-seg {
  display: flex;
  align-items: center;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 0 10px;
  transition: border-color .15s, box-shadow .15s;
}
.slug-seg:focus-within { border-color: var(--accent); box-shadow: 0 0 0 3px var(--ring); }
.slug-seg.invalid { border-color: var(--danger); }
.slug-host { color: var(--faint); font-size: 12.5px; white-space: nowrap; }
.slug-input {
  width: 11.5ch;
  border: none;
  background: transparent;
  color: var(--text);
  font-size: 13.5px;
  padding: 11px 2px;
  transition: width .18s ease;
}
.slug-input:focus, .slug-input:not(:placeholder-shown) { width: 15ch; }

.shorten-btn { padding: 0 20px; min-height: 44px; font-size: 14px; }
.btn-spinner {
  width: 15px; height: 15px;
  border: 2px solid rgba(255,255,255,.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: lk-spin .7s linear infinite;
}
.creator-hint { margin-top: 8px; font-size: 11.5px; color: var(--faint); }

/* ---- Post-upload "add a short link" row ---- */
.add-link-row {
  margin-top: 12px;
  padding: 12px 14px;
  background: var(--card);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  animation: lk-pop .3s ease both;
}
.add-link-label {
  display: block;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--muted);
  margin-bottom: 8px;
}
.add-link-controls { display: flex; gap: 8px; flex-wrap: wrap; }
.add-link-controls .slug-seg { flex: 1; min-width: 180px; }
.add-link-controls .slug-input { flex: 1; width: auto; }

/* ---- Existing-file picker (signed in) ---- */
.file-card-row {
  display: flex;
  gap: 10px;
  padding: 8px;
  background: var(--card);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  animation: lk-pop .35s ease both;
  flex-wrap: wrap;
}
.file-select-wrap {
  flex: 1;
  min-width: 220px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding-left: 12px;
}
.file-select {
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  color: var(--text);
  font-size: 14.5px;
  padding: 12px 0;
  cursor: pointer;
}

.src-toggle { margin-top: 10px; border: none; background: transparent; color: var(--muted); font-size: 12.5px; font-weight: 600; cursor: pointer; padding: 0; }
.src-toggle:hover { color: var(--text); }

.shorten-error { margin-top: 10px; color: var(--danger, #d33); font-size: 12.5px; font-weight: 500; }

.samples {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  margin-top: 16px;
  flex-wrap: wrap;
}
.samples-label { font-size: 12px; color: var(--faint); }
.sample-chip {
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--muted);
  font-size: 11.5px;
  padding: 4px 9px;
  border-radius: var(--radius);
  cursor: pointer;
}
.sample-chip:hover { color: var(--text); border-color: var(--border-strong); }

/* ---- Result ---- */
.result-card {
  background: var(--card);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
  animation: lk-pop .4s ease both;
}
.result-stripe { height: 5px; background: repeating-linear-gradient(90deg, var(--accent) 0 9px, transparent 9px 16px); }
.result-body { padding: 22px 24px; display: flex; gap: 22px; align-items: center; flex-wrap: wrap; }
.result-qr { display: flex; flex-direction: column; align-items: center; gap: 8px; }
.dl-qr { border: none; background: transparent; color: var(--muted); font-size: 12px; font-weight: 600; cursor: pointer; }
.dl-qr:hover { color: var(--text); }
.result-main { flex: 1; min-width: 240px; }
.result-ok { display: flex; align-items: center; gap: 8px; color: var(--success); font-size: 12.5px; font-weight: 600; margin-bottom: 12px; }
.ok-badge { width: 18px; height: 18px; border-radius: 50%; background: var(--success); color: #fff; display: flex; align-items: center; justify-content: center; font-size: 11px; }
.result-link {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 12px 14px;
  margin-bottom: 12px;
}
.result-short { flex: 1; font-size: 16px; font-weight: 600; color: var(--accent); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.result-dest { font-size: 12.5px; color: var(--muted); display: flex; align-items: center; gap: 6px; margin-bottom: 18px; overflow: hidden; }
.result-actions { display: flex; gap: 9px; flex-wrap: wrap; }
.result-claim {
  border-top: 1px solid var(--border);
  background: var(--accent-soft);
  padding: 14px 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.claim-text { font-size: 13px; color: var(--text); font-weight: 500; flex: 1; min-width: 200px; }

.faint { color: var(--faint); }
.ellipsis { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.mono { font-family: var(--font-mono); }

@media (max-width: 560px) {
  .creator-card { gap: 6px; }
  .url-wrap { flex-basis: 100%; }
  .slug-seg { flex: 1; }
  .slug-input { flex: 1; width: auto; }
  .shorten-btn { flex: 1; }
}
</style>
